import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { HeartHandshake, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function QrDialog({ open, onOpenChange, profileId }) {
  const [qr, setQr] = useState(null);
  useEffect(() => {
    if (open && profileId) {
      api.get("/me/qr", { params: { profile_id: profileId } }).then((r) => setQr(r.data)).catch((e) => toast.error(errMsg(e)));
    }
  }, [open, profileId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="qr-dialog">
        <DialogHeader>
          <DialogTitle>QR Peserta</DialogTitle>
          <DialogDescription>Tunjukkan ke keeper untuk identifikasi & check-in manual.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          {qr ? (
            <div className="rounded-2xl bg-white p-4 ring-1 ring-[#1A3323]/10" data-testid="qr-code">
              <QRCodeSVG value={qr.qr} size={220} fgColor="#1A3323" />
            </div>
          ) : (
            <Loader2 className="h-8 w-8 animate-spin" />
          )}
          <div className="font-bold text-[#1A3323]" data-testid="qr-name">{qr?.display_name}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DonationDialog({ open, onOpenChange, eventId, reminder = false, publicMode = false }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (open) api.get(publicMode ? "/public/donation" : "/donation", { params: { event_id: eventId || undefined } }).then((r) => setD(r.data)).catch(() => setD({}));
  }, [open, eventId, publicMode]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-sm overflow-y-auto" data-testid={reminder ? "donation-reminder-dialog" : "donation-dialog"}>
        <DialogHeader>
          {reminder && <div className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#3D6F4B]" data-testid="donation-reminder-tag">Climb selesai, mantap!</div>}
          <DialogTitle className="flex items-center gap-2"><HeartHandshake className="h-5 w-5 text-[#3D6F4B]" /> {reminder ? "Dukung komunitas yuk" : "Kontribusi Kolektif Komunitas"}</DialogTitle>
          <DialogDescription>
            {reminder
              ? "Wall dan alat tetap terawat berkat kontribusi climber. Seikhlasnya lewat QRIS di bawah."
              : d?.account_name || "Komunitas iyalagi.manjat"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          {d?.qris_image ? (
            <img src={d.qris_image} alt="QRIS" className="w-full max-w-[260px] rounded-xl bg-white p-2 ring-1 ring-black/10" data-testid="donation-qris-image" />
          ) : (
            <div className="w-full rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="donation-qris-empty">
              QRIS belum diatur oleh admin.
            </div>
          )}
          <p className="text-sm leading-relaxed text-muted-foreground" data-testid="donation-explanation">{d?.explanation}</p>
          <p className="w-full rounded-lg bg-[#F5F3EB] p-3 text-xs text-[#1A3323]">
            Hanya tampilan QRIS, tanpa verifikasi pembayaran.
          </p>
          {reminder && (
            <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)} data-testid="donation-reminder-later">Nanti saja</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WaiverDialog({ open, onOpenChange, waiver, profile, onAccepted, requireChild = true }) {
  const [agree, setAgree] = useState(false);
  const [child, setChild] = useState(false);
  const [busy, setBusy] = useState(false);
  const isChild = profile?.is_child;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/me/profile/${profile.id}/waiver`, { version: waiver.version, child_confirmed: child });
      toast.success("Waiver disetujui.");
      onAccepted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!waiver) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="waiver-dialog">
        <DialogHeader>
          <DialogTitle>{waiver.title} (v{waiver.version})</DialogTitle>
          <DialogDescription>{isChild ? `Disetujui wali atas nama ${profile.full_name}.` : "Baca dan setujui sebelum berpartisipasi."}</DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto whitespace-pre-line rounded-lg bg-[#F5F3EB] p-4 text-sm leading-relaxed">{waiver.content}</div>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="waiver-agree-checkbox" />
          {isChild ? "Saya, sebagai wali, menyetujui waiver ini." : "Saya telah membaca dan menyetujui waiver ini."}
        </label>
        {isChild && requireChild && (
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={child} onCheckedChange={(v) => setChild(!!v)} data-testid="waiver-child-checkbox" />
            Anak menyatakan memahami aturan keselamatan.
          </label>
        )}
        <Button onClick={submit} disabled={!agree || (isChild && requireChild && !child) || busy} className="h-12" data-testid="waiver-accept-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Setujui Waiver
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function PaidEventWaiverDialog({ open, onOpenChange, event, profile, onAccepted }) {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const waiver = event?.paid_waiver;

  useEffect(() => {
    if (open) {
      setAgree(false);
    }
  }, [open, waiver?.version]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/me/events/${event.id}/paid-waiver`, {
        profile_id: profile.id,
        version: waiver.version,
      });
      toast.success("Waiver khusus event disetujui.");
      onOpenChange(false);
      onAccepted?.();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  if (!waiver) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="paid-event-waiver-dialog">
        <DialogHeader>
          <DialogTitle>{waiver.title}</DialogTitle>
          <DialogDescription>
            Waiver khusus {event.name} · versi {waiver.version}
          </DialogDescription>
        </DialogHeader>
        <div
          className="max-h-64 overflow-y-auto whitespace-pre-line rounded-lg bg-[#F5F3EB]
            p-4 text-sm leading-relaxed"
          data-testid="paid-event-waiver-content"
        >
          {waiver.content}
        </div>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={agree}
            onCheckedChange={(value) => setAgree(!!value)}
            data-testid="paid-event-waiver-agree-checkbox"
          />
          Saya telah membaca dan menyetujui waiver khusus event ini.
        </label>
        <Button
          onClick={submit}
          disabled={!agree || busy}
          className="h-12"
          data-testid="paid-event-waiver-accept-button"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Setujui & Lanjutkan Pendaftaran
        </Button>
      </DialogContent>
    </Dialog>
  );
}
